import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus, ArrowsClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EventDialog from "@/components/workforce/EventDialog";
import EmployeeDialog from "@/components/workforce/EmployeeDialog";
import {
  WORKFORCE_TABS, STATUS_TONE, workforceApi, formatDate, formatDateRange, formatDays, formatBirthday,
} from "@/components/workforce/workforceApi";
import HolidaysTab from "@/components/workforce/HolidaysTab";
import CalendarTab from "@/components/workforce/CalendarTab";
import WorkGroupsTab from "@/components/workforce/WorkGroupsTab";
import OperationsDashboardTab from "@/components/workforce/OperationsDashboardTab";
import CoverageTab from "@/components/workforce/CoverageTab";

/**
 * Coolman Workforce — main page.
 *
 * V1.0 scope (per Product Owner directive 2026-07-03):
 *   • Sean (Administrator) has full edit access.
 *   • Kevin (Owner) has read-only.  Dialogs guard on `readOnly`.
 *   • Everything is a Workforce Event — the Calendar aggregates them.
 *   • Vacation balances follow anniversary years (server-computed).
 *
 * Tab layout mirrors the Product Owner's approved sitemap:
 *   Dashboard · Calendar · Events · Employees · Settings.
 */
export default function Workforce() {
  const { user } = useAuth();
  const readOnly = user?.role === "Owner";
  const [tab, setTab] = useState("dashboard");
  const navigate = useNavigate();

  // Shared state — loaded once, refreshed after every mutation. The
  // Dashboard tab manages its own per-widget fetches (spec 13 § 2.3) so
  // it is NOT part of the shared reload cycle.
  const [meta, setMeta] = useState({ work_groups: [], event_statuses: [] });
  const [employees, setEmployees] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [balances, setBalances] = useState([]);

  const reload = useCallback(async () => {
    try {
      const [m, emps, types, evs, hols, bals] = await Promise.all([
        workforceApi.metadata(),
        workforceApi.employees(),
        workforceApi.eventTypes(),
        workforceApi.events(),
        workforceApi.holidays(),
        workforceApi.balances(),
      ]);
      setMeta(m.data);
      setEmployees(emps.data);
      setEventTypes(types.data);
      setEvents(evs.data);
      setHolidays(hols.data);
      setBalances(bals.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Workforce data");
    }
  }, []);

  // Listen for cross-tab navigation events (SettingsTab and Calendar
  // EventDetail panel dispatch these to switch tabs without prop drilling).
  useEffect(() => {
    const onNav = (e) => {
      const target = e?.detail;
      if (target && WORKFORCE_TABS.some((t) => t.key === target)) setTab(target);
    };
    window.addEventListener("workforce:navigate-tab", onNav);
    return () => window.removeEventListener("workforce:navigate-tab", onNav);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Dialog state
  const [eventDlg, setEventDlg] = useState({ open: false, editing: null });
  const [empDlg, setEmpDlg] = useState({ open: false, editing: null });

  return (
    <div className="min-h-screen bg-surface p-6 md:p-8" data-testid="workforce-page">
      <PageHeader
        title="Coolman Workforce"
        description="Single source of truth for employee availability, time off, and workforce planning."
        actions={
          !readOnly ? (
            <>
              <button
                data-testid="workforce-new-event"
                onClick={() => setEventDlg({ open: true, editing: null })}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-800 bg-charcoal px-3 py-1.5 text-sm font-semibold text-ink-inverse hover:bg-slate-900"
              >
                <Plus size={14} weight="bold" /> New Event
              </button>
            </>
          ) : (
            <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-[11px] font-semibold text-sky-700">
              Read-only view
            </span>
          )
        }
      />

      <div className="mb-6 flex flex-wrap gap-1 border-b border-slate-200">
        {WORKFORCE_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`workforce-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold transition-colors border-b-2 -mb-px ${
              tab === t.key
                ? "border-charcoal text-charcoal"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "dashboard"  && <OperationsDashboardTab onNavigateTab={setTab} onOpenEmployee={(id) => navigate(`/workforce/employees/${id}`)} />}
      {tab === "calendar"   && <CalendarTab employees={employees} eventTypes={eventTypes} meta={meta} onReload={reload} readOnly={readOnly} />}
      {tab === "events"     && <EventsTab events={events} onOpen={(ev) => setEventDlg({ open: true, editing: ev })} onReload={reload} readOnly={readOnly} />}
      {tab === "employees"  && <EmployeesTab employees={employees} balances={balances} onEdit={(e) => setEmpDlg({ open: true, editing: e })} onOpenProfile={(id) => navigate(`/workforce/employees/${id}`)} onReload={reload} readOnly={readOnly} />}
      {tab === "coverage"    && <CoverageTab readOnly={readOnly} employees={employees} />}
      {tab === "work_groups" && <WorkGroupsTab readOnly={readOnly} onReload={reload} />}
      {tab === "holidays"   && <HolidaysTab holidays={holidays} onReload={reload} readOnly={readOnly} />}
      {tab === "settings"   && <SettingsTab eventTypes={eventTypes} onReload={reload} readOnly={readOnly} />}

      <EventDialog
        open={eventDlg.open}
        onClose={() => setEventDlg({ open: false, editing: null })}
        onSaved={reload}
        employees={employees}
        eventTypes={eventTypes}
        editing={eventDlg.editing}
        readOnly={readOnly}
      />
      <EmployeeDialog
        open={empDlg.open}
        onClose={() => setEmpDlg({ open: false, editing: null })}
        onSaved={reload}
        editing={empDlg.editing}
        workGroups={meta.work_groups}
        readOnly={readOnly}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard tab — registry-driven, per-widget independent refresh
// (spec 13 § 2). Each widget owns its own fetch/loading/error state via
// `useWidgetData`; adding widgets is a one-line registry insert plus one
// component file. Personalization (show/hide, order, size, default
// ---------------------------------------------------------------------------
// Legacy dashboard tab replaced by OperationsDashboardTab (WF-103, PO
// directive 2026-02-04). The registry-driven widget infrastructure
// remains available in `components/workforce/widgets/` for reuse
// elsewhere but is no longer the Workforce home page.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Events tab — filterable list
// ---------------------------------------------------------------------------
function EventsTab({ events, onOpen, onReload, readOnly }) {
  const [status, setStatus] = useState("");
  const filtered = useMemo(
    () => (status ? events.filter((e) => e.status === status) : events),
    [status, events],
  );
  return (
    <div data-testid="workforce-events-tab">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {["", "Pending", "Approved", "Declined"].map((s) => (
          <button
            key={s || "all"}
            data-testid={`workforce-events-filter-${s || "all"}`}
            onClick={() => setStatus(s)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${
              status === s
                ? "border-slate-800 bg-charcoal text-ink-inverse"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            {s || "All"}
          </button>
        ))}
      </div>
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2">Employee</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Dates</th>
              <th className="px-3 py-2 text-right">Days</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filtered.map((ev) => (
              <tr
                key={ev.id}
                data-testid={`workforce-event-row-${ev.id}`}
                className="cursor-pointer hover:bg-slate-50"
                onClick={() => onOpen(ev)}
              >
                <td className="px-3 py-2 font-medium text-slate-900">{ev.employee_name}</td>
                <td className="px-3 py-2">
                  <span
                    className="mr-1.5 inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: ev.event_type_colour }}
                  />
                  {ev.event_type_name}
                </td>
                <td className="px-3 py-2 text-slate-700">
                  {formatDateRange(ev.start_date, ev.end_date, ev.start_half, ev.end_half)}
                </td>
                <td className="px-3 py-2 text-right font-mono">{formatDays(ev.duration_days)}</td>
                <td className="px-3 py-2">
                  <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[ev.status]}`}>
                    {ev.status}
                  </span>
                </td>
                <td className="px-3 py-2 max-w-xs truncate text-slate-600">{ev.notes || "—"}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-sm text-slate-500">
                  No events yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Employees tab
// ---------------------------------------------------------------------------
function EmployeesTab({ employees, balances, onEdit, onOpenProfile, onReload, readOnly }) {
  const balByEmp = useMemo(() => Object.fromEntries(balances.map((b) => [b.employee_id, b])), [balances]);
  const [syncing, setSyncing] = useState(false);
  const syncFromUsers = async () => {
    setSyncing(true);
    try {
      const r = await workforceApi.syncUsers();
      toast.success(r.data.added_count > 0 ? `Added ${r.data.added_count} employee(s)` : "Already in sync");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };
  return (
    <div data-testid="workforce-employees-tab">
      <div className="mb-3 flex justify-end gap-2">
        {!readOnly && (
          <>
            <button
              data-testid="workforce-sync-users"
              onClick={syncFromUsers}
              disabled={syncing}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              <ArrowsClockwise size={13} weight="bold" /> {syncing ? "Syncing…" : "Sync from Users"}
            </button>
            <button
              data-testid="workforce-new-employee"
              onClick={() => onEdit(null)}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-800 bg-charcoal px-3 py-1.5 text-sm font-semibold text-ink-inverse hover:bg-slate-900"
            >
              <Plus size={13} weight="bold" /> New Employee
            </button>
          </>
        )}
      </div>
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Work Group</th>
              <th className="px-3 py-2">Department</th>
              <th className="px-3 py-2">Hire Date</th>
              <th className="px-3 py-2">Birthday</th>
              <th className="px-3 py-2 text-right">Vacation Remaining</th>
              <th className="px-3 py-2">Login</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {employees.map((emp) => {
              const b = balByEmp[emp.id];
              return (
                <tr
                  key={emp.id}
                  data-testid={`workforce-emp-row-${emp.id}`}
                  className="cursor-pointer hover:bg-slate-50"
                  onClick={() => onOpenProfile ? onOpenProfile(emp.id) : onEdit(emp)}
                >
                  <td className="px-3 py-2 font-mono text-slate-600" data-testid={`workforce-emp-num-${emp.id}`}>
                    {emp.employee_number ?? "—"}
                  </td>
                  <td className="px-3 py-2 font-medium text-slate-900">{emp.full_name}</td>
                  <td className="px-3 py-2 text-slate-700">{emp.work_group || "—"}</td>
                  <td className="px-3 py-2 text-slate-700">{emp.department || "—"}</td>
                  <td className="px-3 py-2 text-slate-700 font-mono text-[13px]">{formatDate(emp.hire_date)}</td>
                  <td className="px-3 py-2 text-slate-700" data-testid={`workforce-emp-birthday-${emp.id}`}>
                    {formatBirthday(emp.birthday_mm_dd)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {b ? `${formatDays(b.remaining_days)} / ${formatDays(b.entitlement_days)}` : "—"}
                  </td>
                  <td className="px-3 py-2 text-[11px]">
                    {emp.user_id ? (
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700">Linked</span>
                    ) : (
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-slate-500">No login</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11px]">
                    <span className={`rounded-full border px-2 py-0.5 font-semibold ${
                      emp.status === "Active" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-100 text-slate-600"
                    }`}>{emp.status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Calendar tab — month view
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Settings tab — event types only. Holiday Management moved to its own tab
// (PO directive 2026-07-03 · Holiday Management Dashboard).
// ---------------------------------------------------------------------------
function SettingsTab({ eventTypes, onReload, readOnly }) {
  const toggleType = async (t, enabled) => {
    try {
      await workforceApi.saveEventType(t.code, { ...t, enabled });
      toast.success(`${t.name} ${enabled ? "enabled" : "disabled"}`);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <div data-testid="workforce-settings-tab" className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="mb-3 font-display text-base font-semibold text-slate-900">Event Types</h3>
        <p className="mb-3 text-xs text-slate-500">
          Phase 2 event types are pre-configured but disabled. Toggle to enable On-Call, Training, Meetings, and Company Events.
        </p>
        <div className="space-y-1.5">
          {eventTypes.map((t) => (
            <div
              key={t.code}
              data-testid={`wf-settings-type-${t.code}`}
              className="flex items-center justify-between rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
            >
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: t.colour }} />
                <span className="font-medium text-slate-900">{t.name}</span>
                {t.counts_against_vacation && (
                  <span className="rounded-full bg-teal-50 px-1.5 py-0.5 text-[10px] text-teal-700">counts against vacation</span>
                )}
              </div>
              <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  disabled={readOnly}
                  checked={t.enabled}
                  onChange={(e) => toggleType(t, e.target.checked)}
                />
                Enabled
              </label>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h3 className="mb-3 font-display text-base font-semibold text-slate-900">Holiday Management</h3>
        <p className="text-sm text-slate-600">
          Holiday Management now has its own dedicated tab.{" "}
          <button
            type="button"
            data-testid="wf-settings-goto-holidays"
            className="font-semibold text-slate-900 underline hover:text-slate-700"
            onClick={() => window.dispatchEvent(new CustomEvent("workforce:navigate-tab", { detail: "holidays" }))}
          >
            Open Holidays tab →
          </button>
        </p>
      </section>
    </div>
  );
}
